import React, { useState, useEffect } from 'react';
import api, { formatApiErrorDetail } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import NotificationModal from '../components/NotificationModal';

const Usuarios = () => {
  const { user: currentUser } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [selectedUsuario, setSelectedUsuario] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRol, setFilterRol] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // Estados para notificaciones
  const [notification, setNotification] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info',
    onConfirm: null
  });

  // Campos del formulario
  const [formData, setFormData] = useState({
    tipo_persona: 'Jurídica',
    ruc: '',
    razon_social: '',
    email: '',
    celular: '',
    direccion: '',
    rol: 2,
    estado: true,
    is_email_verified: true,
    password: ''
  });

  // Cargar usuarios al montar
  useEffect(() => {
    loadUsuarios();
  }, []);

  const loadUsuarios = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/v1/usuarios/');
      setUsuarios(response.data || []);
    } catch (err) {
      console.error('Error loading usuarios:', err);
      setError('Error al cargar los usuarios');
      setUsuarios([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      tipo_persona: 'Jurídica',
      ruc: '',
      razon_social: '',
      email: '',
      celular: '',
      direccion: '',
      rol: 2,
      estado: true,
      is_email_verified: true,
      password: ''
    });
    setEditingId(null);
    setSelectedUsuario(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);

      if (editingId) {
        // Actualizar usuario existente
        await api.put(`/api/v1/usuarios/${editingId}`, formData);
        setNotification({
          show: true,
          title: 'Éxito',
          message: 'Usuario actualizado exitosamente',
          type: 'success',
          onConfirm: null
        });
      } else {
        // Crear nuevo usuario - requiere password
        if (!formData.password) {
          setNotification({
            show: true,
            title: 'Campo requerido',
            message: 'La contraseña es requerida para crear un nuevo usuario',
            type: 'warning',
            onConfirm: null
          });
          setLoading(false);
          return;
        }
        const usuarioData = { ...formData };
        await api.post('/api/v1/usuarios/', usuarioData);
        setNotification({
          show: true,
          title: 'Éxito',
          message: 'Usuario creado exitosamente',
          type: 'success',
          onConfirm: null
        });
      }

      loadUsuarios();
      setShowForm(false);
      resetForm();
    } catch (err) {
      console.error('Error saving usuario:', err);
      const errorMsg = formatApiErrorDetail(err, 'Error al guardar el usuario');
      setError(errorMsg);
      setNotification({
        show: true,
        title: 'Error',
        message: errorMsg,
        type: 'error',
        onConfirm: null
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (usuario) => {
    setFormData({
      tipo_persona: usuario.tipo_persona || 'Jurídica',
      ruc: usuario.ruc || '',
      razon_social: usuario.razon_social || '',
      email: usuario.email || '',
      celular: usuario.celular || '',
      direccion: usuario.direccion || '',
      rol: usuario.rol || 2,
      estado: usuario.estado !== undefined ? usuario.estado : true,
      is_email_verified: usuario.is_email_verified !== undefined ? usuario.is_email_verified : true,
      password: ''
    });
    setEditingId(usuario.id_usuario);
    setShowForm(true);
  };

  const handleView = async (id) => {
    try {
      const response = await api.get(`/api/v1/usuarios/${id}`);
      setSelectedUsuario(response.data);
      setShowDetails(true);
    } catch (err) {
      console.error('Error loading usuario:', err);
      setNotification({
        show: true,
        title: 'Error',
        message: 'Error al cargar los detalles del usuario',
        type: 'error',
        onConfirm: null
      });
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setNotification({
        show: true,
        title: 'Contraseña inválida',
        message: 'La contraseña debe tener al menos 6 caracteres',
        type: 'warning',
        onConfirm: null
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setNotification({
        show: true,
        title: 'Contraseñas no coinciden',
        message: 'Las contraseñas no coinciden',
        type: 'warning',
        onConfirm: null
      });
      return;
    }

    try {
      setLoading(true);
      await api.put(`/api/v1/usuarios/${selectedUsuario.id_usuario}/reset-password`, {
        new_password: newPassword
      });
      setNotification({
        show: true,
        title: 'Éxito',
        message: 'Contraseña actualizada exitosamente',
        type: 'success',
        onConfirm: null
      });
      setShowResetPassword(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Error resetting password:', err);
      const errorMsg = formatApiErrorDetail(err, 'Error al resetear la contraseña');
      setNotification({
        show: true,
        title: 'Error',
        message: errorMsg,
        type: 'error',
        onConfirm: null
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (id === currentUser?.id_usuario) {
      setNotification({
        show: true,
        title: 'Acción no permitida',
        message: 'No puede eliminar su propio usuario',
        type: 'warning',
        onConfirm: null
      });
      return;
    }

    setNotification({
      show: true,
      title: 'Confirmar eliminación',
      message: '¿Está seguro de que desea eliminar este usuario?',
      type: 'confirm',
      onConfirm: async () => {
        try {
          setLoading(true);
          await api.delete(`/api/v1/usuarios/${id}`);
          setNotification({
            show: true,
            title: 'Éxito',
            message: 'Usuario eliminado exitosamente',
            type: 'success',
            onConfirm: null
          });
          loadUsuarios();
        } catch (err) {
          console.error('Error deleting usuario:', err);
          const errorMsg = formatApiErrorDetail(err, 'Error al eliminar el usuario');
          setNotification({
            show: true,
            title: 'Error',
            message: errorMsg,
            type: 'error',
            onConfirm: null
          });
        } finally {
          setLoading(false);
        }
      },
      showCancel: true
    });
  };

  // Filtrar usuarios
  const filteredUsuarios = usuarios.filter((usr) => {
    const matchesSearch = !searchTerm ||
      (usr.razon_social && usr.razon_social.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (usr.email && usr.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (usr.ruc && usr.ruc.includes(searchTerm));

    const matchesRol = !filterRol || usr.rol?.toString() === filterRol;

    const matchesEstado = filterEstado === '' ||
      (filterEstado === 'true' && usr.estado === true) ||
      (filterEstado === 'false' && usr.estado === false);

    return matchesSearch && matchesRol && matchesEstado;
  });

  const getRolName = (rol) => {
    const roles = {
      1: 'Administrador',
      2: 'Proveedor',
      3: 'Evaluador'
    };
    return roles[rol] || `Rol ${rol}`;
  };

  if (loading && usuarios.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3 text-muted">Cargando usuarios...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h3 mb-1 fw-bold">Usuarios</h1>
          <p className="text-muted mb-0">Gestión de usuarios del sistema</p>
        </div>
        <button
          onClick={() => {
            setShowForm(!showForm);
            if (!showForm) {
              resetForm();
            }
          }}
          className={`btn ${showForm ? 'btn-secondary' : 'btn-primary'}`}
        >
          <i className={`bi ${showForm ? 'bi-x-circle' : 'bi-plus-circle'} me-2`}></i>
          {showForm ? 'Cancelar' : 'Nuevo Usuario'}
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Formulario */}
      {showForm && (
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '10px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          marginBottom: '2rem'
        }}>
          <h2>{editingId ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">Tipo de Persona *</label>
                <select
                  className="form-select"
                  value={formData.tipo_persona}
                  onChange={(e) => setFormData({ ...formData, tipo_persona: e.target.value })}
                  required
                >
                  <option value="Natural">Natural</option>
                  <option value="Jurídica">Jurídica</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">RUC *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.ruc}
                  onChange={(e) => setFormData({ ...formData, ruc: e.target.value })}
                  required
                  maxLength={11}
                  placeholder="12345678901"
                />
              </div>
              <div className="col-md-12">
                <label className="form-label">Razón Social *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.razon_social}
                  onChange={(e) => setFormData({ ...formData, razon_social: e.target.value })}
                  required
                  placeholder="Nombre o razón social"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  className="form-control"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="usuario@ejemplo.com"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Celular *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.celular}
                  onChange={(e) => setFormData({ ...formData, celular: e.target.value })}
                  required
                  maxLength={9}
                  placeholder="987654321"
                />
              </div>
              <div className="col-md-12">
                <label className="form-label">Dirección</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.direccion}
                  onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                  placeholder="Dirección completa"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Rol *</label>
                <select
                  className="form-select"
                  value={formData.rol}
                  onChange={(e) => setFormData({ ...formData, rol: parseInt(e.target.value) })}
                  required
                >
                  <option value={1}>Administrador</option>
                  <option value={2}>Proveedor</option>
                  <option value={3}>Evaluador</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Estado *</label>
                <select
                  className="form-select"
                  value={formData.estado ? 'true' : 'false'}
                  onChange={(e) => setFormData({ ...formData, estado: e.target.value === 'true' })}
                  required
                >
                  <option value="true">Activo</option>
                  <option value="false">Inactivo</option>
                </select>
              </div>
              {!editingId && (
                <>
                  <div className="col-md-6">
                    <label className="form-label">Contraseña *</label>
                    <input
                      type="password"
                      className="form-control"
                      value={formData.password || ''}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required={!editingId}
                      minLength={6}
                      placeholder="Mínimo 6 caracteres"
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Email Verificado *</label>
                    <select
                      className="form-select"
                      value={formData.is_email_verified ? 'true' : 'false'}
                      onChange={(e) => setFormData({ ...formData, is_email_verified: e.target.value === 'true' })}
                      required
                    >
                      <option value="true">Sí (Verificado)</option>
                      <option value="false">No (No verificado)</option>
                    </select>
                    <small className="form-text text-muted">
                      Los usuarios creados por admin pueden tener email verificado por defecto
                    </small>
                  </div>
                </>
              )}
            </div>
            <div className="mt-4">
              <button type="submit" className="btn btn-primary me-2" disabled={loading}>
                {loading ? 'Guardando...' : editingId ? 'Actualizar Usuario' : 'Crear Usuario'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => {
                setShowForm(false);
                resetForm();
              }}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filtros */}
      <div style={{
        background: 'white',
        padding: '1.5rem',
        borderRadius: '10px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h5 className="mb-3">Filtros de búsqueda</h5>
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label">Búsqueda general</label>
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por razón social, email o RUC..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Filtrar por Rol</label>
            <select
              className="form-select"
              value={filterRol}
              onChange={(e) => setFilterRol(e.target.value)}
            >
              <option value="">Todos los roles</option>
              <option value="1">Administrador</option>
              <option value="2">Proveedor</option>
              <option value="3">Evaluador</option>
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Filtrar por Estado</label>
            <select
              className="form-select"
              value={filterEstado}
              onChange={(e) => setFilterEstado(e.target.value)}
            >
              <option value="">Todos los estados</option>
              <option value="true">Activo</option>
              <option value="false">Inactivo</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabla de usuarios */}
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '10px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">
            Lista de Usuarios ({filteredUsuarios.length})
          </h5>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={loadUsuarios}
          >
            <i className="bi bi-arrow-clockwise me-2"></i>
            Actualizar
          </button>
        </div>

        {filteredUsuarios.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
            {usuarios.length === 0
              ? 'No hay usuarios registrados aún.'
              : 'No se encontraron usuarios con los filtros aplicados.'}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#667eea', color: 'white' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #ddd' }}>ID</th>
                  <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #ddd' }}>Razón Social</th>
                  <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #ddd' }}>RUC</th>
                  <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #ddd' }}>Email</th>
                  <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #ddd' }}>Rol</th>
                  <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #ddd' }}>Estado</th>
                  <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #ddd' }}>Fecha Registro</th>
                  <th style={{ padding: '1rem', textAlign: 'center', border: '1px solid #ddd' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsuarios.map((usr) => (
                  <tr key={usr.id_usuario}>
                    <td style={{ padding: '1rem', border: '1px solid #ddd' }}>{usr.id_usuario}</td>
                    <td style={{ padding: '1rem', border: '1px solid #ddd' }}>{usr.razon_social}</td>
                    <td style={{ padding: '1rem', border: '1px solid #ddd' }}>{usr.ruc}</td>
                    <td style={{ padding: '1rem', border: '1px solid #ddd' }}>{usr.email}</td>
                    <td style={{ padding: '1rem', border: '1px solid #ddd' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        background: usr.rol === 1 ? '#c6f6d5' : '#bee3f8',
                        color: '#2d3748',
                        fontWeight: '500',
                        fontSize: '0.875rem'
                      }}>
                        {getRolName(usr.rol)}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', border: '1px solid #ddd' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        background: usr.estado ? '#c6f6d5' : '#fed7d7',
                        color: '#2d3748',
                        fontWeight: '500',
                        fontSize: '0.875rem'
                      }}>
                        {usr.estado ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', border: '1px solid #ddd' }}>
                      {usr.fecha_registro
                        ? new Date(usr.fecha_registro).toLocaleDateString('es-PE')
                        : 'N/A'}
                    </td>
                    <td style={{ padding: '1rem', border: '1px solid #ddd', textAlign: 'center' }}>
                      <div className="d-flex gap-2 justify-content-center">
                        <button
                          onClick={() => handleView(usr.id_usuario)}
                          className="btn btn-info btn-sm"
                          style={{ fontSize: '0.875rem' }}
                          title="Ver detalles"
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                        <button
                          onClick={() => handleEdit(usr)}
                          className="btn btn-primary btn-sm"
                          style={{ fontSize: '0.875rem' }}
                          title="Editar"
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          onClick={() => {
                            setSelectedUsuario(usr);
                            setShowResetPassword(true);
                          }}
                          className="btn btn-warning btn-sm"
                          style={{ fontSize: '0.875rem' }}
                          title="Resetear contraseña"
                        >
                          <i className="bi bi-key"></i>
                        </button>
                        <button
                          onClick={() => handleDelete(usr.id_usuario)}
                          className="btn btn-danger btn-sm"
                          style={{ fontSize: '0.875rem' }}
                          disabled={usr.id_usuario === currentUser?.id_usuario}
                          title={usr.id_usuario === currentUser?.id_usuario ? "No puede eliminar su propio usuario" : "Eliminar"}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Detalles */}
      {showDetails && selectedUsuario && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '10px',
            padding: '2rem',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            width: '100%'
          }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 className="h4 mb-1 fw-bold">Detalles del Usuario</h2>
                <p className="text-muted mb-0">#{selectedUsuario.id_usuario}</p>
              </div>
              <button
                onClick={() => {
                  setShowDetails(false);
                  setSelectedUsuario(null);
                }}
                className="btn btn-outline-secondary"
              >
                <i className="bi bi-x-circle me-2"></i>
                Cerrar
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <strong>Tipo de Persona:</strong> {selectedUsuario.tipo_persona || 'N/A'}
              </div>
              <div>
                <strong>RUC:</strong> {selectedUsuario.ruc || 'N/A'}
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <strong>Razón Social:</strong> {selectedUsuario.razon_social || 'N/A'}
              </div>
              <div>
                <strong>Email:</strong> {selectedUsuario.email || 'N/A'}
              </div>
              <div>
                <strong>Celular:</strong> {selectedUsuario.celular || 'N/A'}
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <strong>Dirección:</strong> {selectedUsuario.direccion || 'N/A'}
              </div>
              <div>
                <strong>Rol:</strong> {getRolName(selectedUsuario.rol)}
              </div>
              <div>
                <strong>Estado:</strong>{' '}
                <span style={{
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  background: selectedUsuario.estado ? '#c6f6d5' : '#fed7d7',
                  color: '#2d3748',
                  fontWeight: '500'
                }}>
                  {selectedUsuario.estado ? 'Activo' : 'Inactivo'}
                </span>
              </div>
              <div>
                <strong>Fecha de Registro:</strong>{' '}
                {selectedUsuario.fecha_registro
                  ? new Date(selectedUsuario.fecha_registro).toLocaleString('es-PE')
                  : 'N/A'}
              </div>
              {selectedUsuario.fecha_edicion && (
                <div>
                  <strong>Fecha de Edición:</strong>{' '}
                  {new Date(selectedUsuario.fecha_edicion).toLocaleString('es-PE')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Resetear Contraseña */}
      {showResetPassword && selectedUsuario && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '10px',
            padding: '2rem',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 className="h4 mb-1 fw-bold">Resetear Contraseña</h2>
                <p className="text-muted mb-0">{selectedUsuario.razon_social}</p>
              </div>
              <button
                onClick={() => {
                  setShowResetPassword(false);
                  setSelectedUsuario(null);
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="btn btn-outline-secondary"
              >
                <i className="bi bi-x-circle me-2"></i>
                Cerrar
              </button>
            </div>

            <div className="mb-3">
              <label className="form-label">Nueva Contraseña *</label>
              <input
                type="password"
                className="form-control"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                minLength={6}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Confirmar Contraseña *</label>
              <input
                type="password"
                className="form-control"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repita la contraseña"
                minLength={6}
                required
              />
            </div>

            <div className="d-flex gap-2">
              <button
                onClick={handleResetPassword}
                className="btn btn-primary"
                disabled={loading || !newPassword || !confirmPassword}
              >
                {loading ? 'Actualizando...' : 'Actualizar Contraseña'}
              </button>
              <button
                onClick={() => {
                  setShowResetPassword(false);
                  setSelectedUsuario(null);
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                className="btn btn-secondary"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Notificaciones */}
      <NotificationModal
        show={notification.show}
        onClose={() => setNotification({ ...notification, show: false })}
        title={notification.title}
        message={notification.message}
        type={notification.type}
        onConfirm={notification.onConfirm}
        showCancel={notification.type === 'confirm'}
      />
    </div>
  );
};

export default Usuarios;
